/**
 * PrivateNotesService — reads and writes a student's Private file
 * (`privateNotes/{studentId}/entries`). Model, tags and validation:
 * privateNotesModel.js. Rules: firestore.rules → Private file.
 *
 * Staff only, by rule, in both directions: any teacher with viewStudents reads
 * and adds; editing and deleting are admin / owner only. Nothing here is ever
 * reachable from a family surface.
 */
import {
	collection,
	doc,
	addDoc,
	updateDoc,
	deleteDoc,
	onSnapshot,
	query,
	orderBy,
	serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import people from '../canon/people.json';
import { knownTags, MAX_PRIVATE_NOTE_LENGTH } from './privateNotesModel';

const entriesRef = (studentId) => collection(db, 'privateNotes', studentId, 'entries');

/**
 * The people a private note can mention: the active staff in people.json
 * (Grace, Anthony, Annelise), in canon order. people.json is already in the
 * bundle (taskAssignees reads it), so this adds nothing public.
 */
export const MENTIONABLE_STAFF = people.people
	.filter((p) => p.active)
	.sort((a, b) => a.order - b.order)
	.map((p) => ({ uid: p.authUid, name: p.displayName, firstName: p.displayName.split(/\s+/)[0] }));

export const STAFF_UIDS = MENTIONABLE_STAFF.map((p) => p.uid);

export const staffFirstName = (uid) => MENTIONABLE_STAFF.find((p) => p.uid === uid)?.firstName || null;

/** One listener per student, newest first. The file is small (a few entries a week). */
export function subscribePrivateNotes(studentId, onData, onError) {
	const q = query(entriesRef(studentId), orderBy('createdAt', 'desc'));
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => onError && onError(err)
	);
}

export async function addPrivateNote(studentId, entry) {
	const ref = await addDoc(entriesRef(studentId), { ...entry, createdAt: serverTimestamp() });
	return ref.id;
}

/** Admin / owner only (the rules refuse anyone else). */
export async function editPrivateNote(studentId, entryId, { text, tags }, editorUid) {
	const body = String(text || '').trim();
	if (!body) throw new Error('A private note cannot be empty. Delete it instead.');
	if (body.length > MAX_PRIVATE_NOTE_LENGTH) throw new Error(`The limit is ${MAX_PRIVATE_NOTE_LENGTH} characters.`);
	await updateDoc(doc(db, 'privateNotes', studentId, 'entries', entryId), {
		text: body,
		tags: knownTags(tags),
		editedAt: serverTimestamp(),
		editedBy: editorUid || null,
	});
}

/** Admin / owner only (the rules refuse anyone else). */
export async function deletePrivateNote(studentId, entryId) {
	await deleteDoc(doc(db, 'privateNotes', studentId, 'entries', entryId));
}
